import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, Edit, ChevronUp, ChevronDown, User } from 'lucide-react';
import type { ExpertProfile, ExpertProfileFilters } from '@/types/expert-profile';

interface ExpertProfilesTableProps {
  profiles: ExpertProfile[];
  isLoading: boolean;
  isEmpty?: boolean;
  sortBy: ExpertProfileFilters['sortBy'];
  sortOrder: ExpertProfileFilters['sortOrder'];
  onSort: (column: ExpertProfileFilters['sortBy']) => void;
}

function SortableHeader({
  column,
  label,
  sortBy,
  sortOrder,
  onSort,
}: {
  column: ExpertProfileFilters['sortBy'];
  label: string;
  sortBy: ExpertProfileFilters['sortBy'];
  sortOrder: ExpertProfileFilters['sortOrder'];
  onSort: (column: ExpertProfileFilters['sortBy']) => void;
}) {
  const isActive = sortBy === column;
  return (
    <th className="px-4 py-3 text-left">
      <button
        className="flex items-center gap-1 text-sm font-medium text-pine-ink-soft hover:text-pine-ink"
        onClick={() => onSort(column)}
      >
        <span>{label}</span>
        <div className="flex flex-col -space-y-1">
          <ChevronUp className={`w-3 h-3 ${isActive && sortOrder === 'asc' ? 'text-pine-pine' : ''}`} />
          <ChevronDown className={`w-3 h-3 ${isActive && sortOrder === 'desc' ? 'text-pine-pine' : ''}`} />
        </div>
      </button>
    </th>
  );
}

function ExpertProfileStatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string }> = {
    active: {
      label: 'Active',
      className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    },
    inactive: {
      label: 'Inactive',
      className: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
    },
  };
  const c = config[status] || config.inactive;

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border ${c.className}`}>
      {c.label}
    </span>
  );
}

export default function ExpertProfilesTable({
  profiles,
  isLoading,
  isEmpty = false,
  sortBy,
  sortOrder,
  onSort,
}: ExpertProfilesTableProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="admin-card overflow-hidden">
        <div className="animate-pulse space-y-4 p-6">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-pine-paper rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="admin-card p-12 text-center">
        <h3 className="text-lg font-semibold text-pine-ink">No expert profiles yet</h3>
        <p className="text-pine-ink-soft mt-2">Get started by creating your first expert profile</p>
        <button
          onClick={() => navigate('/admin/expert-profiles/new')}
          className="admin-button-primary mt-4"
        >
          Create Your First Expert Profile
        </button>
      </div>
    );
  }

  if (profiles.length === 0) {
    return (
      <div className="admin-card p-12 text-center">
        <h3 className="text-lg font-semibold text-pine-ink">No expert profiles found</h3>
        <p className="text-pine-ink-soft mt-2">Try adjusting your filters</p>
      </div>
    );
  }

  return (
    <div className="admin-card overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-pine-line">
            <th className="px-4 py-3 text-left text-sm font-medium text-pine-ink-soft">Avatar</th>
            <SortableHeader column="name" label="Name" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
            <th className="px-4 py-3 text-left text-sm font-medium text-pine-ink-soft">Subtitle</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-pine-ink-soft">Status</th>
            <SortableHeader column="createdAt" label="Created" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
            <th className="px-4 py-3 text-right text-sm font-medium text-pine-ink-soft">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-pine-line">
          {profiles.map((profile, index) => (
            <motion.tr
              key={profile.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="hover:bg-pine-hover-item transition-colors"
            >
              <td className="px-4 py-3">
                {profile.avatarUrl ? (
                  <img
                    src={profile.avatarUrl}
                    alt={profile.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-pine-paper flex items-center justify-center">
                    <User className="w-5 h-5 text-pine-ink-soft" />
                  </div>
                )}
              </td>
              <td className="px-4 py-3">
                <button
                  className="text-pine-ink hover:text-pine-pine font-medium text-left"
                  onClick={() => navigate(`/admin/expert-profiles/${profile.id}`)}
                >
                  {profile.name}
                </button>
              </td>
              <td className="px-4 py-3 text-pine-ink-soft text-sm">
                {profile.subtitle}
              </td>
              <td className="px-4 py-3">
                <ExpertProfileStatusBadge status={profile.status} />
              </td>
              <td className="px-4 py-3 text-pine-ink-soft text-sm">
                {new Date(profile.createdAt).toLocaleDateString()}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => navigate(`/admin/expert-profiles/${profile.id}`)}
                    className="p-1.5 rounded-lg hover:bg-pine-hover-item text-pine-ink-soft hover:text-pine-ink transition-colors"
                    title="View"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => navigate(`/admin/expert-profiles/${profile.id}/edit`)}
                    className="p-1.5 rounded-lg hover:bg-pine-hover-item text-pine-ink-soft hover:text-pine-ink transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
